import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { Col, Input, Row, Typography } from 'antd'
import { Icon } from '@iconify/react'
import { useSelector } from 'react-redux'

import TalentList from './components/TalentList'
import TalentListTableView from './components/TalentListTableView'
import TalentPermissionsDrawer from './components/TalentPermissionsDrawer'
import { getSupportUsers } from './talent.service'
import { loginState } from '../login/login.slice'
import { getTalentAccess } from '../../helpers/reportsPermission'
import styles from './talent.module.css'

const { Title } = Typography
const PAGE_SIZE = 20

const Talent = () => {
    const { userProfile } = useSelector(loginState)
    const talentAccess = useMemo(
        () => getTalentAccess(userProfile),
        [userProfile]
    )
    const [users, setUsers] = useState([])
    const [search, setSearch] = useState('')
    const [apiSearch, setApiSearch] = useState('')
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [isNext, setIsNext] = useState(false)
    const [permissionsUser, setPermissionsUser] = useState(null)
    const [permissionsOpen, setPermissionsOpen] = useState(false)
    const [viewMode, setViewMode] = useState('card')

    const loadUsers = useCallback(
        async ({ skip = 0, search: searchTerm = apiSearch } = {}) => {
            const isLoadMore = skip > 0
            if (isLoadMore) {
                setLoadingMore(true)
            } else {
                setLoading(true)
            }

            try {
                const { users: nextUsers, isNext: nextIsNext } =
                    await getSupportUsers({
                        skip,
                        limit: PAGE_SIZE,
                        search: searchTerm,
                    })

                setUsers((prev) =>
                    isLoadMore ? [...prev, ...nextUsers] : nextUsers
                )
                setIsNext(nextIsNext)
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [apiSearch]
    )

    useEffect(() => {
        loadUsers({ skip: 0, search: apiSearch })
    }, [apiSearch, loadUsers])

    const debouncedSearch = useCallback(
        debounce((value) => {
            setApiSearch(value.trim())
        }, 400),
        []
    )

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const handleSearchChange = (event) => {
        const value = event.target.value
        setSearch(value)
        debouncedSearch(value)
    }

    const handleLoadMore = () => {
        if (!isNext || loadingMore) return
        loadUsers({ skip: users.length, search: apiSearch })
    }

    const handleManagePermissions = (user) => {
        if (!talentAccess.canEdit) return
        setPermissionsUser(user)
        setPermissionsOpen(true)
    }

    const handleClosePermissions = () => {
        setPermissionsOpen(false)
        setPermissionsUser(null)
    }

    return (
        <div className={styles.pageContainer}>
            <div className={styles.pageHeader}>
                <Row
                    justify="space-between"
                    align="middle"
                    wrap
                    gutter={[16, 12]}
                    style={{
                        borderBottom: '1px solid #e2e8f0',
                        padding: '14px 24px 14px 24px',
                    }}
                >
                    <Col flex="none">
                        <Title level={3} className={styles.pageTitle}>
                            Talent
                        </Title>
                    </Col>
                    <Col flex="1 1 280px" style={{ minWidth: 0 }}>
                        <div className={styles.controls}>
                            <div className={styles.searchWrap}>
                                <Input
                                    allowClear
                                    size="large"
                                    placeholder="Search talent user by name, email, or phone"
                                    className={styles.search}
                                    prefix={
                                        <Icon
                                            icon="mdi:magnify"
                                            width={18}
                                            height={18}
                                        />
                                    }
                                    onChange={handleSearchChange}
                                    value={search}
                                />
                            </div>

                            <div className={styles.viewToggle}>
                                <button
                                    type="button"
                                    className={`${styles.viewToggleBtn} ${
                                        viewMode === 'card'
                                            ? styles.viewToggleBtnActive
                                            : ''
                                    }`}
                                    onClick={() => setViewMode('card')}
                                >
                                    <Icon
                                        icon="mdi:view-grid-outline"
                                        width={16}
                                    />
                                    Card
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.viewToggleBtn} ${
                                        viewMode === 'table'
                                            ? styles.viewToggleBtnActive
                                            : ''
                                    }`}
                                    onClick={() => setViewMode('table')}
                                >
                                    <Icon
                                        icon="mdi:format-list-bulleted"
                                        width={16}
                                    />
                                    List
                                </button>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>

            <div id="talent-scroll-container" className={styles.talentContent}>
                {viewMode === 'table' ? (
                    <TalentListTableView
                        users={users}
                        loading={loading}
                        loadingMore={loadingMore}
                        isNext={isNext}
                        onLoadMore={handleLoadMore}
                        onManagePermissions={handleManagePermissions}
                        canEdit={talentAccess.canEdit}
                    />
                ) : (
                    <TalentList
                        users={users}
                        loading={loading}
                        loadingMore={loadingMore}
                        isNext={isNext}
                        onLoadMore={handleLoadMore}
                        onManagePermissions={handleManagePermissions}
                        canEdit={talentAccess.canEdit}
                    />
                )}
            </div>

            <TalentPermissionsDrawer
                open={permissionsOpen}
                user={permissionsUser}
                onClose={handleClosePermissions}
            />
        </div>
    )
}

export default Talent
